import React from 'react';
import { TABLE_CONFIG } from '../../../../constants/enums';
import '../css/Pagination.css';
import { useTranslation } from "react-i18next";

const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalItems, 
  itemsPerPage, 
  onPageChange, 
  onItemsPerPageChange 
}) => {
  const { t } = useTranslation();
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = TABLE_CONFIG.MAX_VISIBLE_PAGES;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }
    }
    
    return pages;
  };
  
  const getPaginationInfo = (currentPage, itemsPerPage, totalItems) => {
    const hasItems = totalItems > 0;
    const startItem = hasItems ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endItem = hasItems
      ? Math.min(currentPage * itemsPerPage, totalItems)
      : 0;

    return {
      startItem,
      endItem,
      totalItems,
    };
  };

  const paginationInfo = getPaginationInfo(currentPage, itemsPerPage, totalItems);

  const handlePageClick = (page) => {
    if (typeof page === 'number' && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePreviousClick = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextClick = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const handleItemsPerPageChange = (e) => {
    onItemsPerPageChange(Number(e.target.value));
  };

  return (
    <div className="pagination-container">
      <div className="pagination-info">
        <span className="items-info">
          {t("admin.pagination.showing", {
            start: paginationInfo.startItem,
            end: paginationInfo.endItem,
            total: paginationInfo.totalItems
          })}        
        </span>
        <div className="items-per-page">
          <label htmlFor="itemsPerPage">{t("admin.pagination.show")}</label>
          <select
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="items-select"
          >
            {TABLE_CONFIG.ITEMS_PER_PAGE_OPTIONS.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span>{t("admin.pagination.per_page")}</span>
        </div>
      </div>

      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={handlePreviousClick}
          disabled={currentPage === 1}
          aria-label={t("admin.pagination.aria_prev")}
        >
          {t("admin.pagination.previous")}
        </button>

        <div className="page-numbers">
          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              className={`page-btn ${page === currentPage ? 'active' : ''} ${page === '...' ? 'dots' : ''}`}
              onClick={() => handlePageClick(page)}
              disabled={page === '...'}
              aria-label={
                typeof page === "number"
                  ? t("admin.pagination.aria_page", { page })
                  : t("admin.pagination.aria_more")
              }>
              {page}
            </button>
          ))}
        </div>

        <button
          className="pagination-btn"
          onClick={handleNextClick}
          disabled={currentPage === totalPages}
          aria-label={t("admin.pagination.aria_next")}
        >
          {t("admin.pagination.next")}
        </button>
      </div>
    </div>
  );
};

export default Pagination;